import {
  Button,
  Drawer,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useContext, useMemo, useState, FC } from "react";
import { Box } from "@mui/system";
import { useSnackbar } from "notistack";
import {
  useAddTenant,
  usePartnersList,
  useUpdateTenant,
  TENANTS_LIST,
} from "@/hooks";
import { useTextDomainContext } from "@/app/providers";
import type { DropdownOption } from "@/types";
import {
  CountryDropdown,
  DataLimitDropdown,
  MonthsDropdown,
  PartnerDropdown,
  SingleDatePicker,
  StatusDropdown,
  SubscriptionDropdown,
} from "@/components";
import { AddTenantProps, TenantRow, Tenant } from "./types";

interface TenantFormProps {
  row?: TenantRow | Partial<Tenant>;
  handleClose: () => void;
}

const TenantForm: FC<TenantFormProps> = ({ row, handleClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const currentDate = useMemo(() => new Date(), []);

  const { partnerList } = usePartnersList();

  const activePartners = useMemo(
    () => partnerList.filter((partner) => partner.status === "Active"),
    [partnerList],
  );

  const defaultSubscriptionExpireAt = useMemo(() => {
    return new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      currentDate.getDate(),
    );
  }, [currentDate]);

  const [inputName, setInputName] = useState<string>(() => row?.name || "");
  const [inputDescription, setInputDescription] = useState<string>(
    () => row?.description || "",
  );
  const [inputContactPerson, setInputContactPerson] = useState<string>(
    () => row?.contactPerson || row?.contact_person || "",
  );
  const [inputInvoiceContactPerson, setInputInvoiceContactPerson] =
    useState<string>(
      () => row?.invoiceContactPerson || row?.invoice_contact_person || "",
    );
  const [inputCompanyNumber, setInputCompanyNumber] = useState<string>(
    () => row?.companyNumber || row?.company_number || "",
  );
  const [inputAddress1, setInputAddress1] = useState<string>(
    () => row?.address1 || row?.address_1 || "",
  );
  const [inputAddress2, setInputAddress2] = useState<string>(
    () => row?.address2 || row?.address_2 || "",
  );
  const [inputCountry, setInputCountry] = useState<string | DropdownOption>(
    () => row?.country || "",
  );
  const [inputWebsite, setInputWebsite] = useState<string>(
    () => row?.website || "",
  );
  const [inputMonth, setInputMonth] = useState<string | DropdownOption>(
    () => row?.financialMonth || row?.month || "",
  );
  const [inputStatus, setInputStatus] = useState<DropdownOption>(() => {
    if (typeof row?.status === "string") {
      return { value: row.status, label: row.status };
    }
    return row?.status || { value: "Active", label: "Active" };
  });
  const [inputSubscriptionPlan, setInputSubscriptionPlan] = useState<
    string | DropdownOption
  >(() => row?.subscriptionPlan || "");
  const [inputSubscriptionExpireAt, setInputSubscriptionExpireAt] = useState<
    Date | undefined
  >(() => {
    if (row?.subscriptionExpireAt) {
      return new Date(row.subscriptionExpireAt);
    }
    return defaultSubscriptionExpireAt;
  });
  const [inputPartners, setInputPartners] = useState<string[]>(() => {
    if (
      row?.partners &&
      Array.isArray(row.partners) &&
      row.partners.length > 0
    ) {
      return row.partners.filter(Boolean) as string[];
    }
    if (row?.partner) {
      return [row.partner];
    }
    return activePartners.length > 0 ? [activePartners[0].id] : [];
  });
  const [inputDataLimit, setInputDataLimit] = useState<string | DropdownOption>(
    () => row?.dataLimit || "",
  );

  const { addTenant, loading: updatingAdd } = useAddTenant(
    () => {
      handleClose();
      enqueueSnackbar(gettext("The new Corporate Group has been added."), {
        variant: "success",
      });
    },
    (error) => {
      if (error === "409") {
        // setEmailError(true);
      } else if (error === "410") {
        enqueueSnackbar(
          gettext("You are not authorized to perform this action"),
          {
            variant: "error",
          },
        );
      } else {
        enqueueSnackbar(gettext("Error: Something went wrong!"), {
          variant: "error",
        });
      }
    },
  );

  const { updateTenant, loading: updatingEdit } = useUpdateTenant(
    () => {
      handleClose();
      enqueueSnackbar(gettext("The Corporate Group has been updated."), {
        variant: "success",
      });
    },
    (error) => {
      if (error === "409") {
        // setEmailError(true);
      } else if (error === "410") {
        enqueueSnackbar(
          gettext("You are not authorized to perform this action"),
          {
            variant: "error",
          },
        );
      } else {
        enqueueSnackbar(gettext("Error: Something went wrong!"), {
          variant: "error",
        });
      }
    },
  );

  const submitHandler = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const variables: {
      name: string;
      description: string;
      contactPerson: string;
      invoiceContactPerson: string;
      companyNumber: string;
      address1: string;
      address2: string;
      country: string;
      website: string;
      financialMonth: string;
      subscriptionPlan: string;
      dataLimit: string;
      subscriptionExpireAt: Date | undefined;
      partner: string;
      partners: string[];
      status: string;
      tenantId?: string;
    } = {
      name: inputName,
      description: inputDescription,
      contactPerson: inputContactPerson,
      invoiceContactPerson: inputInvoiceContactPerson,
      companyNumber: inputCompanyNumber,
      address1: inputAddress1,
      address2: inputAddress2,
      country:
        typeof inputCountry === "object"
          ? String(inputCountry?.value)
          : String(inputCountry || ""),
      website: inputWebsite,
      financialMonth:
        typeof inputMonth === "object"
          ? String(inputMonth?.value)
          : String(inputMonth || ""),
      subscriptionPlan:
        typeof inputSubscriptionPlan === "object"
          ? String(inputSubscriptionPlan?.value)
          : String(inputSubscriptionPlan || ""),
      dataLimit:
        typeof inputDataLimit === "object"
          ? String(inputDataLimit?.value)
          : String(inputDataLimit || ""),
      subscriptionExpireAt: inputSubscriptionExpireAt,
      partner: inputPartners[0] || "",
      partners: inputPartners,
      status:
        typeof inputStatus === "object"
          ? String(inputStatus?.value)
          : String(inputStatus || ""),
    };

    if (row?.id) {
      variables.tenantId = row.id;
      updateTenant({
        variables: variables,
        refetchQueries: [{ query: TENANTS_LIST }],
      }).then();
    } else {
      addTenant({
        variables: variables,
        refetchQueries: [{ query: TENANTS_LIST }],
      }).then();
    }
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputName(event.target.value);
  };

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setInputDescription(event.target.value);
  };

  const handleContactPersonChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setInputContactPerson(event.target.value);
  };

  const handleInvoiceContactPersonChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setInputInvoiceContactPerson(event.target.value);
  };

  const handleCompanyNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setInputCompanyNumber(event.target.value);
  };

  const handleAddress1Change = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputAddress1(event.target.value);
  };

  const handleAddress2Change = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputAddress2(event.target.value);
  };

  const handleCountryChange = (event: string | DropdownOption) => {
    setInputCountry(event);
  };

  const handleWebsiteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputWebsite(event.target.value);
  };

  const handleMonthChange = (event: string | DropdownOption) => {
    setInputMonth(event);
  };

  const handleSubscriptionPlanChange = (event: string | DropdownOption) => {
    setInputSubscriptionPlan(event);
  };

  const handleDataLimitChange = (event: string | DropdownOption) => {
    setInputDataLimit(event);
  };

  const handleSubscriptionExpireAtChange = (event: Date) => {
    setInputSubscriptionExpireAt(event);
  };

  const handleStatusChange = (
    event:
      | string
      | DropdownOption
      | import("@/hooks/common/useStatusArray").StatusOption,
  ) => {
    if (typeof event === "string") {
      setInputStatus({ value: event, label: event });
    } else if (typeof (event as any)?.value !== "undefined") {
      const e: any = event as any;
      setInputStatus({
        ...(e as Record<string, unknown>),
        value: String(e.value),
        label: e.label || String(e.value),
      });
    } else {
      setInputStatus(event as unknown as DropdownOption);
    }
  };

  const handlePartnerChange = (event: any) => {
    if (Array.isArray(event)) {
      const selected = event
        .map((item: any) => {
          if (typeof item === "object" && item !== null) {
            return String(item.value ?? item.id ?? "");
          }
          return String(item);
        })
        .filter(Boolean);
      setInputPartners(selected);
    } else if (event && typeof event === "object") {
      const val = String(event.value ?? event.id ?? "");
      setInputPartners(val ? [val] : []);
    } else if (typeof event === "string" && event) {
      setInputPartners([event]);
    } else {
      setInputPartners([]);
    }
  };

  return (
    <form onSubmit={submitHandler}>
      <Stack
        spacing={4}
        sx={{
          height: "100%",
        }}
      >
        <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
          {!row?.id && gettext("Add New Corporate Group")}
          {row?.id && gettext("Edit Corporate Group")}
        </Typography>

        <Box
          sx={{
            height: "100%",
          }}
        >
          <Grid container spacing={4} sx={{ height: "100%" }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                type="text"
                label={gettext("Corporate Group")}
                placeholder={gettext("Corporate Group")}
                size="small"
                required
                fullWidth
                value={inputName}
                onChange={handleNameChange}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                type="text"
                multiline
                maxRows={4}
                label={gettext("Description")}
                placeholder={gettext("Description")}
                size="small"
                fullWidth
                value={inputDescription}
                onChange={handleDescriptionChange}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                type="text"
                label={gettext("Contact Person")}
                placeholder={gettext("Contact Person")}
                size="small"
                fullWidth
                value={inputContactPerson}
                onChange={handleContactPersonChange}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                type="text"
                label={gettext("Invoice Contact Person")}
                placeholder={gettext("Invoice Contact Person")}
                size="small"
                fullWidth
                value={inputInvoiceContactPerson}
                onChange={handleInvoiceContactPersonChange}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                type="text"
                label={gettext("Company Number")}
                placeholder={gettext("Company Number")}
                size="small"
                fullWidth
                value={inputCompanyNumber}
                onChange={handleCompanyNumberChange}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                type="text"
                label={gettext("Address 1")}
                placeholder={gettext("Address 1")}
                size="small"
                fullWidth
                value={inputAddress1}
                onChange={handleAddress1Change}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                type="text"
                label={gettext("Address 2")}
                placeholder={gettext("Address 2")}
                size="small"
                fullWidth
                value={inputAddress2}
                onChange={handleAddress2Change}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <CountryDropdown
                defaultValue={
                  typeof inputCountry === "object"
                    ? inputCountry?.value
                    : inputCountry
                }
                onChange={handleCountryChange}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                type="text"
                label={gettext("Website")}
                placeholder={gettext("Website")}
                size="small"
                fullWidth
                value={inputWebsite}
                onChange={handleWebsiteChange}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <MonthsDropdown
                componentLabel={gettext("Financial Year Start")}
                defaultValue={
                  typeof inputMonth === "object"
                    ? inputMonth?.value
                    : inputMonth
                }
                visualStyle={"autoComplete"}
                showAll={false}
                onChange={handleMonthChange}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PartnerDropdown
                multiple
                componentLabel={gettext("Partners")}
                defaultValue={inputPartners}
                onChange={handlePartnerChange}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <SubscriptionDropdown
                defaultValue={
                  typeof inputSubscriptionPlan === "object"
                    ? String(inputSubscriptionPlan?.value)
                    : String(inputSubscriptionPlan || "")
                }
                componentLabel={gettext("Subscription Plan")}
                onChange={handleSubscriptionPlanChange}
                required
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <DataLimitDropdown
                defaultValue={
                  typeof inputDataLimit === "object"
                    ? String(inputDataLimit?.value)
                    : String(inputDataLimit || "")
                }
                onChange={handleDataLimitChange}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <SingleDatePicker
                componentLabel={gettext("Expire At")}
                selectedDate={inputSubscriptionExpireAt?.toString() || ""}
                onValueChange={handleSubscriptionExpireAtChange}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <StatusDropdown
                onChange={handleStatusChange}
                defaultValue={
                  typeof inputStatus === "object"
                    ? String(inputStatus.value)
                    : String(inputStatus || "")
                }
                showAll={false}
                visualStyle="autoComplete"
              />
            </Grid>
          </Grid>
        </Box>

        <Paper elevation={0}>
          <Grid container spacing={4} sx={{ width: "100%" }}>
            <Grid size={{ xs: 6 }} sx={{ textAlign: "center" }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => handleClose()}
              >
                {gettext("Cancel")}
              </Button>
            </Grid>

            <Grid size={{ xs: 6 }} sx={{ textAlign: "center" }}>
              <Button
                variant="contained"
                loading={updatingAdd || updatingEdit}
                fullWidth
                type="submit"
              >
                {gettext("Save")}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Stack>
    </form>
  );
};

export const AddTenant: FC<AddTenantProps> = (props) => {
  const open = Boolean(props.open);

  const handleClose = () => {
    props.handleClose(false);
  };

  return (
    <Drawer
      anchor={"right"}
      open={open}
      onClose={handleClose}
      sx={{
        zIndex: (theme) => theme.zIndex.appBar + 1,
      }}
      slotProps={{
        paper: {
          sx: {
            padding: 4,
            maxWidth: 450,
          },
        },
      }}
    >
      <Box
        sx={{
          width: "auto",
        }}
      >
        {open && (
          <TenantForm
            key={props.row?.id ?? "new"}
            row={props.row}
            handleClose={handleClose}
          />
        )}
      </Box>
    </Drawer>
  );
};
